import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { Trash2, Edit, Plus } from 'lucide-react';
import { Database } from '@/integrations/supabase/types';

// Extend the WhatsAppTemplate type to include the new template_id field
type WhatsAppTemplateBase = Database['public']['Tables']['whatsapp_templates']['Row'];
type WhatsAppTemplate = WhatsAppTemplateBase & { template_id?: string };

// Extend the insert and update types as well
type WhatsAppTemplateInsertBase = Database['public']['Tables']['whatsapp_templates']['Insert'];
type WhatsAppTemplateInsert = WhatsAppTemplateInsertBase & { template_id?: string };

type WhatsAppTemplateUpdateBase = Database['public']['Tables']['whatsapp_templates']['Update'];
type WhatsAppTemplateUpdate = WhatsAppTemplateUpdateBase & { template_id?: string };

interface NewTemplateForm {
  nombre: string;
  categoria: string;
  idioma: string;
  texto: string;
  estado: string;
  variables_count: number;
  description: string;
  template_id: string;
}

export const WhatsAppTemplatesManager: React.FC = () => {
  const [templates, setTemplates] = useState<WhatsAppTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<WhatsAppTemplate | null>(null);
  const [form, setForm] = useState<NewTemplateForm>({
    nombre: '',
    categoria: 'Utility',
    idioma: 'es',
    texto: '',
    estado: 'ACTIVO',
    variables_count: 0,
    description: '',
    template_id: ''
  });
  const { toast } = useToast();

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('whatsapp_templates')
        .select('*')
        .order('nombre');

      if (error) {
        throw error;
      }

      setTemplates(data || []);
    } catch (error) {
      console.error('Error fetching templates:', error);
      toast({
        title: 'Error',
        description: 'No se pudieron cargar las plantillas',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTemplates();
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    if (name === 'variables_count') {
      const numValue = parseInt(value) || 0;
      setForm(prev => ({ ...prev, [name]: numValue }));
    } else {
      setForm(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSelectChange = (name: string, value: string) => {
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const resetForm = () => {
    setForm({
      nombre: '',
      categoria: 'Utility',
      idioma: 'es',
      texto: '',
      estado: 'ACTIVO',
      variables_count: 0,
      description: '',
      template_id: ''
    });
    setEditingTemplate(null);
  };

  const handleOpenDialog = (template?: WhatsAppTemplate) => {
    if (template) {
      setEditingTemplate(template);
      setForm({
        nombre: template.nombre,
        categoria: template.categoria,
        idioma: template.idioma,
        texto: template.texto,
        estado: template.estado,
        variables_count: template.variables_count || 0,
        description: template.description || '',
        template_id: template.template_id || ''
      });
    } else {
      resetForm();
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    resetForm();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      if (editingTemplate) {
        // Update existing template
        const updateData: WhatsAppTemplateUpdate = {
          nombre: form.nombre,
          categoria: form.categoria,
          idioma: form.idioma,
          texto: form.texto,
          estado: form.estado,
          variables_count: form.variables_count,
          description: form.description,
          template_id: form.template_id,
          updated_at: new Date().toISOString()
        };
        
        const { error } = await supabase
          .from('whatsapp_templates')
          .update(updateData)
          .eq('id', editingTemplate.id);

        if (error) throw error;
        
        toast({
          title: 'Plantilla actualizada',
          description: 'La plantilla ha sido actualizada correctamente'
        });
      } else {
        // Create new template
        const insertData: WhatsAppTemplateInsert = {
          nombre: form.nombre,
          categoria: form.categoria,
          idioma: form.idioma,
          texto: form.texto,
          estado: form.estado,
          variables_count: form.variables_count,
          description: form.description,
          template_id: form.template_id,
          botones: []
        };
        
        const { error } = await supabase
          .from('whatsapp_templates')
          .insert(insertData);

        if (error) throw error;
        
        toast({
          title: 'Plantilla creada',
          description: 'La plantilla ha sido creada correctamente'
        });
      }
      
      handleCloseDialog();
      fetchTemplates();
    } catch (error) {
      console.error('Error saving template:', error);
      toast({
        title: 'Error',
        description: 'No se pudo guardar la plantilla',
        variant: 'destructive'
      });
    }
  };

  const handleDeleteTemplate = async (id: string) => {
    if (!confirm('¿Estás seguro de que quieres eliminar esta plantilla?')) {
      return;
    }
    
    try {
      const { error } = await supabase
        .from('whatsapp_templates')
        .delete()
        .eq('id', id);

      if (error) throw error;
      
      toast({
        title: 'Plantilla eliminada',
        description: 'La plantilla ha sido eliminada correctamente'
      });
      
      fetchTemplates();
    } catch (error) {
      console.error('Error deleting template:', error);
      toast({
        title: 'Error',
        description: 'No se pudo eliminar la plantilla',
        variant: 'destructive'
      });
    }
  };

  const handleToggleStatus = async (template: WhatsAppTemplate) => {
    const newStatus = template.estado === 'ACTIVO' ? 'INACTIVO' : 'ACTIVO';
    
    try {
      const { error } = await supabase
        .from('whatsapp_templates')
        .update({ estado: newStatus })
        .eq('id', template.id);

      if (error) throw error;
      
      toast({
        title: 'Estado actualizado',
        description: `La plantilla ahora está ${newStatus.toLowerCase()}`
      });
      
      fetchTemplates();
    } catch (error) {
      console.error('Error updating status:', error);
      toast({
        title: 'Error',
        description: 'No se pudo actualizar el estado',
        variant: 'destructive'
      });
    }
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>Plantillas de WhatsApp</CardTitle>
        <Button onClick={() => handleOpenDialog()} size="sm">
          <Plus className="h-4 w-4 mr-2" /> Nueva plantilla
        </Button>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-primary"></div>
          </div>
        ) : templates.length > 0 ? (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nombre</TableHead>
                <TableHead>Identificador</TableHead>
                <TableHead>Categoría</TableHead>
                <TableHead>Variables</TableHead>
                <TableHead>Estado</TableHead>
                <TableHead className="text-right">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {templates.map((template) => (
                <TableRow key={template.id}>
                  <TableCell className="font-medium">{template.nombre}</TableCell>
                  <TableCell>{template.template_id || '-'}</TableCell>
                  <TableCell>{template.categoria}</TableCell>
                  <TableCell>{template.variables_count}</TableCell>
                  <TableCell>
                    <Badge variant={template.estado === 'ACTIVO' ? 'default' : 'secondary'}>
                      {template.estado}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={() => handleToggleStatus(template)}
                      >
                        {template.estado === 'ACTIVO' ? 'Desactivar' : 'Activar'}
                      </Button>
                      <Button 
                        variant="outline" 
                        size="icon" 
                        onClick={() => handleOpenDialog(template)}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button 
                        variant="outline" 
                        size="icon" 
                        className="text-red-500 hover:text-red-600"
                        onClick={() => handleDeleteTemplate(template.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : (
          <div className="text-center py-8 text-gray-500">
            No hay plantillas registradas
          </div>
        )}

        <Dialog open={openDialog} onOpenChange={setOpenDialog}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>{editingTemplate ? 'Editar plantilla' : 'Nueva plantilla'}</DialogTitle>
              <DialogDescription>
                {editingTemplate 
                  ? 'Actualiza los detalles de la plantilla existente' 
                  : 'Crea una nueva plantilla para mensajes de WhatsApp'}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="nombre">Nombre</Label>
                  <Input
                    id="nombre"
                    name="nombre"
                    value={form.nombre}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="template_id">Identificador en Twilio</Label>
                  <Input
                    id="template_id"
                    name="template_id"
                    value={form.template_id}
                    onChange={handleInputChange}
                    placeholder="ID de plantilla en Twilio"
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="categoria">Categoría</Label>
                  <Select 
                    value={form.categoria} 
                    onValueChange={(value) => handleSelectChange('categoria', value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Selecciona una categoría" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Utility">Utility</SelectItem>
                      <SelectItem value="Marketing">Marketing</SelectItem>
                      <SelectItem value="Authentication">Authentication</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="idioma">Idioma</Label>
                  <Input
                    id="idioma"
                    name="idioma"
                    value={form.idioma}
                    onChange={handleInputChange}
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="texto">Texto</Label>
                <Textarea
                  id="texto"
                  name="texto"
                  value={form.texto}
                  onChange={handleInputChange}
                  rows={5}
                  required
                />
                <p className="text-xs text-gray-500">
                  Usa {"{1}"}, {"{2}"}, etc. como marcadores para variables.
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="variables_count">Número de variables</Label>
                  <Input
                    id="variables_count"
                    name="variables_count"
                    type="number"
                    min="0"
                    value={form.variables_count}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="estado">Estado</Label>
                  <Select 
                    value={form.estado} 
                    onValueChange={(value) => handleSelectChange('estado', value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Selecciona un estado" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ACTIVO">ACTIVO</SelectItem>
                      <SelectItem value="INACTIVO">INACTIVO</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Descripción</Label>
                <Textarea
                  id="description"
                  name="description"
                  value={form.description}
                  onChange={handleInputChange}
                  rows={3}
                  placeholder="Describe el propósito de la plantilla y sus variables"
                />
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={handleCloseDialog}>
                  Cancelar
                </Button>
                <Button type="submit">
                  {editingTemplate ? 'Guardar cambios' : 'Crear plantilla'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
}; 